import { useQuery } from '@apollo/client/react'
import isEmpty from 'lodash-es/isEmpty'
import { ReactElement, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'

import {
  LmfAnalyticsProvider,
  LoadingSpinner,
  ViewportSizes,
  useIsViewport,
} from '@syconium/little-miss-figgy'

import { DocumentType, gql } from '../../../../../__generated__/graphql/catalog'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { useCartStore } from '../../../../../lib/adapters/cart-persistence/useCartStore'

import { BundleUpsellTile } from './BundleUpsellTile'
import { CarouselControls } from './CarouselControls'
import { Body, LoadingSpinnerWrapper, Title, TitleWrapper } from './styles'

import type { Swiper as SwiperInstance } from 'swiper'

export const BUNDLE_RECOMMENDATION_FIELDS = gql(`
  fragment BundleRecommendationFields on BundleRecommendation {
    sku
    title
    colorDisplayValue
    colorId
    sizeDisplayValue
    sizeId
    imageUrl
    urlPath
    availableForSale
    shopifyVariantId
    shopifyProductId
    productHandle
    primaryCategory {
      id
    }
    finalSale
    siteWidePromoEligible
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
      currencyCode
    }
  }
`)

export type Recommendation = DocumentType<typeof BUNDLE_RECOMMENDATION_FIELDS>

export const FETCH_CART_BUNDLE_PROGRAMS = gql(`
  query FetchCartBundlePrograms($cartId: String!) {
    cartBundlePrograms(cartId: $cartId) {
      completeTheBundle {
        bundleProgramHandle
        minQty
        totalQty
        collectionId
        recommendations {
          ...BundleRecommendationFields
        }
      }
    }
  }
`)

export const BundleUpsells = ({ handle }: { handle: string }): ReactElement | null => {
  const [swiper, setSwiper] = useState<SwiperInstance | null>(null)

  const {
    cart: {
      bundle: { completeYourBundle },
    },
    magnolia: {
      general: { loading: loadingText },
    },
  } = useTranslation()

  const {
    cart: { id: cartId },
  } = useCartStore()

  const isMediumOrLargeViewport = useIsViewport([ViewportSizes.medium, ViewportSizes.large])
  const isLargeViewPort = useIsViewport([ViewportSizes.large])

  const { data, loading } = useQuery(FETCH_CART_BUNDLE_PROGRAMS, {
    skip: !cartId,
    variables: { cartId: cartId! },
  })

  const completeTheBundle = data?.cartBundlePrograms.completeTheBundle?.find(
    ({ bundleProgramHandle }) => bundleProgramHandle === handle
  )

  const recommendations = !isEmpty(completeTheBundle?.recommendations)
    ? completeTheBundle?.recommendations.filter(rec => Boolean(rec.availableForSale))
    : []

  if (isEmpty(recommendations) && !loading) {
    return null
  } else {
    return (
      <Body>
        <TitleWrapper>
          <Title>{completeYourBundle}</Title>
          {isMediumOrLargeViewport && <CarouselControls swiper={swiper} />}
        </TitleWrapper>
        {loading ? (
          <LoadingSpinnerWrapper>
            <LoadingSpinner label={loadingText} size='24px' />
          </LoadingSpinnerWrapper>
        ) : (
          <LmfAnalyticsProvider title='Bundle Upsells Slider'>
            <Swiper
              onSwiper={setSwiper}
              slidesPerView={isLargeViewPort ? 1.4 : 1.15}
              spaceBetween={12}
              slidesOffsetAfter={12}
              allowTouchMove={!isMediumOrLargeViewport}
            >
              {recommendations!.map(recommendation => {
                return (
                  <SwiperSlide key={recommendation.sku}>
                    <LmfAnalyticsProvider title='Bundle Upsells Slide'>
                      <BundleUpsellTile recommendation={recommendation} />
                    </LmfAnalyticsProvider>
                  </SwiperSlide>
                )
              })}
            </Swiper>
          </LmfAnalyticsProvider>
        )}
      </Body>
    )
  }
}
